import {
  EndOfLeaseStepContainer,
  SummaryStepContainer as SummaryStepContainerEoLDCC,
} from '@ngb-frontend/features/end-of-lease-dcc'
import {
  DashboardStepContainer,
  MileageStepContainer,
  ServiceStepContainer,
  MalfunctionStepContainer,
  InspectionStepContainer,
  ExtraServiceStepContainer,
  DateStepContainer,
  ContactStepContainer,
  SummaryStepContainer,
  ReplacementVehicleStepContainer,
  LocationStepContainer,
  DriverServiceLocationStepContainer,
} from '@ngb-frontend/features/maintenance'
import {
  MaintenanceAndRepairStepContainer,
  SummaryStepContainer as SummaryStepContainerMnRDCC,
} from '@ngb-frontend/features/maintenance-dcc'
import { StepVariants } from '@ngb-frontend/shared/types'
import { ServiceLocationStepContainer } from '@ngb-frontend/shared/ui'

import type { Steps, Feature } from '@ngb-frontend/shared/types'

const cKeys = {
  locationTitle: 'steps.location.title',
  locationNavLabel: 'steps.location.navLabel',
  driverNavLabel: 'steps.summary.navLabelDriver',
}

export const EOL_DCC_STEPS: Steps<Feature.EoLDCC> = [
  {
    component: EndOfLeaseStepContainer,
    path: 'eol-dcc/end-of-lease',
    navLabel: 'steps.endOfLease.navLabel',
    title: 'steps.endOfLease.title',
  },
  {
    component: ServiceLocationStepContainer,
    path: 'eol-dcc/service-location',
    fullWidth: true,
    variant: {
      [StepVariants.ServiceLocation]: {
        navLabel: 'steps.selectServiceLocation.navLabel',
        title: 'steps.selectServiceLocation.title',
      },
      [StepVariants.ServiceLocationWithPickup]: {
        navLabel: 'steps.selectServiceLocationWithPickup.navLabel',
        title: 'steps.selectServiceLocationWithPickup.title',
      },
    },
  },
  {
    component: SummaryStepContainerEoLDCC,
    path: 'eol-dcc/summary',
    navLabel: 'steps.endOfLeaseSummary.navLabel',
    title: 'steps.endOfLeaseSummary.title',
    variant: {
      [StepVariants.Overview]: {
        routeToBack: true,
      },
    },
  },
]

export const MNR_DCC_STEPS: Steps<Feature.MnRDCC> = [
  {
    component: MaintenanceAndRepairStepContainer,
    path: 'dcc/maintenance-and-repair',
    navLabel: 'steps.maintenanceAndRepair.navLabel',
    title: 'steps.maintenanceAndRepair.title',
  },
  {
    component: ServiceLocationStepContainer,
    path: 'dcc/service-location',
    fullWidth: true,
    navLabel: cKeys.locationNavLabel,
    variant: {
      [StepVariants.ServiceLocation]: {
        navLabel: 'steps.selectServiceLocation.navLabel',
        title: 'steps.selectServiceLocation.title',
      },
      [StepVariants.ServiceLocationWithPickup]: {
        navLabel: 'steps.selectServiceLocationWithPickup.navLabel',
        title: 'steps.selectServiceLocationWithPickup.title',
      },
    },
  },
  {
    component: SummaryStepContainerMnRDCC,
    path: 'dcc/summary',
    navLabel: 'steps.summary.navLabel',
    title: 'steps.summary.title',
    variant: {
      [StepVariants.Overview]: {
        routeToBack: true,
      },
    },
  },
]

// Driver journey footer presets for the sticky navigation bar. Every step
// advances with the same single "Next" button; back navigation stays on the
// top nav chevron.
// `hiddenFooter` = auto-advance card steps (selection advances by itself, so
// there is no button left to show and the bar is not rendered at all).
const stepFooter = {
  showPrevious: false,
  nextLabel: 'steps.shared.next',
} as const
const hiddenFooter = { showPrevious: false, showNext: false } as const

export const DRIVER_STEPS: Steps<Feature.MnR> = [
  {
    component: DashboardStepContainer,
    path: 'dashboard',
    navLabel: 'steps.dashboard.navLabel',
    // No title or stepper: the banner carries the headline
    navigation: 'brand',
    fullWidth: true,
  },
  {
    component: MileageStepContainer,
    path: 'mileage',
    navLabel: cKeys.driverNavLabel,
    title: 'steps.mileage.title',
    subtitle: 'steps.mileage.subtitle',
    narrow: true,
    stepperIndex: 0,
    footer: stepFooter,
  },
  {
    component: ServiceStepContainer,
    path: 'services',
    navLabel: cKeys.driverNavLabel,
    title: 'steps.service.title',
    subtitle: 'steps.service.subtitle',
    narrow: true,
    flowVariant: 'services',
    stepperIndex: 1,
    footer: stepFooter,
  },
  {
    component: MalfunctionStepContainer,
    path: 'malfunctions',
    navLabel: cKeys.driverNavLabel,
    title: 'steps.malfunction.title',
    subtitle: 'steps.malfunction.subtitle',
    narrow: true,
    stepperIndex: 1,
    footer: stepFooter,
  },
  {
    component: InspectionStepContainer,
    path: 'inspection',
    navLabel: cKeys.driverNavLabel,
    title: 'steps.inspection.title',
    narrow: true,
    stepperIndex: 1,
    footer: stepFooter,
  },
  {
    component: ExtraServiceStepContainer,
    path: 'extra-services',
    navLabel: cKeys.driverNavLabel,
    title: 'steps.extraService.title',
    subtitle: 'steps.extraService.subtitle',
    narrow: true,
    flowVariant: 'extra-services',
    stepperIndex: 1,
    footer: stepFooter,
  },
  {
    component: ReplacementVehicleStepContainer,
    path: 'replacement-vehicle',
    navLabel: cKeys.driverNavLabel,
    title: 'steps.replacementVehicle.title',
    subtitle: 'steps.extraService.subtitle',
    narrow: true,
    stepperIndex: 1,
    footer: stepFooter,
  },
  {
    component: DateStepContainer,
    path: 'date',
    narrow: true,
    variant: {
      [StepVariants.Date]: {
        navLabel: cKeys.driverNavLabel,
        title: 'steps.date.title',
        footer: stepFooter,
      },
      [StepVariants.DateWithPickup]: {
        navLabel: cKeys.driverNavLabel,
        title: 'steps.date.title',
        footer: stepFooter,
      },
    },
    stepperIndex: 2,
  },
  {
    component: LocationStepContainer,
    path: 'location',
    narrow: true,
    variant: {
      [StepVariants.Location]: {
        navLabel: cKeys.driverNavLabel,
        title: cKeys.locationTitle,
        subtitle: 'steps.location.subtitle',
        footer: stepFooter,
      },
      [StepVariants.LocationWithPickup]: {
        navLabel: cKeys.driverNavLabel,
        title: 'steps.locationWithPickup.title',
        footer: stepFooter,
      },
    },
    stepperIndex: 2,
  },
  {
    component: DriverServiceLocationStepContainer,
    path: 'select-service-location',
    navLabel: cKeys.driverNavLabel,
    title: 'steps.selectServiceLocation.titleDriver',
    subtitle: 'steps.selectServiceLocation.subtitle',
    narrow: true,
    stepperIndex: 2,
    variant: {
      [StepVariants.ServiceLocation]: {
        navLabel: cKeys.driverNavLabel,
        title: 'steps.selectServiceLocation.titleDriver',
        footer: stepFooter,
      },
      [StepVariants.ServiceLocationWithPickup]: {
        navLabel: cKeys.driverNavLabel,
        title: 'steps.selectServiceLocationWithPickup.title',
        footer: stepFooter,
      },
    },
  },
  {
    component: ContactStepContainer,
    path: 'contact',
    navLabel: cKeys.driverNavLabel,
    title: 'steps.contact.title',
    subtitle: 'steps.contact.subtitle',
    narrow: true,
    stepperIndex: 2,
    footer: stepFooter,
  },
  {
    component: SummaryStepContainer,
    path: 'summary',
    navLabel: cKeys.driverNavLabel,
    // Design: summary shows only "Confirm my request" (back is via the top nav
    // chevron / per-section Edit links).
    footer: {
      nextLabel: 'steps.summary.submitButtonTextDriver',
      showPrevious: false,
    },
    variant: {
      [StepVariants.Overview]: {
        routeToBack: true,
        // Overview keeps its specialised change/cancel/continue footer inside
        // the page, so the navigation bar has nothing to render.
        footer: hiddenFooter,
      },
    },
    stepperIndex: 3,
  },
]
